import { Ng2StateDeclaration, Transition } from '@uirouter/angular';
import { AllUsersProgressComponent } from '../common/components/all-users-progress/all-users-progress.component';
import { LazyLoadedStates, SessionService } from '@edl-development/edl-common';
import { PortalHelpersService } from 'src/app/services/portal-helpers/portal-helpers.service';
import { AllUsersProgressResolver, CheckCompliancePermissions, CheckRequestPermissions, resolveCurrentPage } from '../common/resolver/all-users-progress.resolver';
import { MyComplianceItemsComponent } from 'src/app/my-compliance-items/my-compliance-items.component';
import { RequestsTabComponent } from '../common/components/requests-tab/requests-tab.component';
import { SubmissionsComponent } from '../common/components/submissions/submissions.component';
import { SubmissionsSortField } from '../common/enums/submissions-sort-field.enum';
import { ComplianceItemCycleComponent } from 'src/app/my-compliance-items/compliance-item-cycle/compliance-item-cycle/compliance-item-cycle.component';
import { CplUserCycleDetails } from 'src/app/model/cpl-user-cycle-details.model';

export type AUPStates<T extends Ng2StateDeclaration | LazyLoadedStates> = {
    userComplianceDetails: T,
    userComplianceDetailsCompliance: T,
    userComplianceDetailsRequests: T,
    userComplianceDetailsSubmissions: T,
    userCycle: T,
}

export function generateSharedAUPLazyLoadedStates(baseStateName: string, baseUrl: string, options: { forCurrentUser: boolean }): AUPStates<LazyLoadedStates> {
    const states = generateSharedAUPStates(baseStateName, baseUrl, options);
    return Object.keys(states).reduce((acc, key) => {
        const state = states[key];
        const lazyState: LazyLoadedStates = {
            name: state.name,
            url: state.url,
            abstract: state.abstract,
            params: state.params
        };

        acc[key] = lazyState;
        return acc;
    }, {} as AUPStates<LazyLoadedStates>);
}

export function generateSharedAUPStatesList(baseStateName: string, baseUrl: string, options: { forCurrentUser: boolean }): Ng2StateDeclaration[] {
    const states = generateSharedAUPStates(baseStateName, baseUrl, options);
    return Object.values(states);
}

function generateSharedAUPStates(baseStateName: string, baseUrl: string, options: { forCurrentUser: boolean }): AUPStates<Ng2StateDeclaration> {
    return {
        userComplianceDetails: {
            name: baseStateName,
            url: baseUrl,
            views: {
                'content@authenticated': {
                    component: AllUsersProgressComponent
                }
            },
            redirectTo: (trans: Transition) => {
                var sessionService: SessionService = trans.injector().get(SessionService);
                var portalHelpersService: PortalHelpersService = trans.injector().get(PortalHelpersService);
                const portalSettings = sessionService.PortalSettings;

                return portalHelpersService.canViewComplianceItems(portalSettings) ? `${baseStateName}.compliance` : `${baseStateName}.requests`;
            },
            data: { requiresAuth: true },
            resolve: [
                {
                    // Used to determine whether to on my-items or all-users-progress versions of shared states, so that we can resolve the userId accordingly for my-items (current user) vs all-users-progress (user from url)
                    token: 'isMyItems',
                    resolveFn: () => options.forCurrentUser
                },
                {
                    // Not using by any inputs to components directly, but used as an input to other resolvers, so that we only have to resolve the userId once for all shared states
                    token: 'aupUserId',
                    deps: [Transition, SessionService],
                    resolveFn: (transition: Transition, sessionService: SessionService) => {
                        const { userId } = transition.params();
                        return options.forCurrentUser ? sessionService.user.UserID : userId;
                    }
                },
                {
                    token: 'user',
                    deps: ['aupUserId', AllUsersProgressResolver],
                    resolveFn: (aupUserId: string, allUsersProgressResolver: AllUsersProgressResolver) => allUsersProgressResolver.resolveAccountByUserId(aupUserId)
                },
                {
                    token: 'userStatusSummary',
                    deps: ['aupUserId', AllUsersProgressResolver],
                    resolveFn: (aupUserId: string, allUsersProgressResolver: AllUsersProgressResolver) =>
                        allUsersProgressResolver.resolveCplUserStatusSummary(aupUserId)
                },
                {
                    token: 'itemPackets',
                    deps: ['aupUserId', AllUsersProgressResolver, Transition],
                    resolveFn: (aupUserId: string, allUsersProgressResolver: AllUsersProgressResolver) => {
                        return allUsersProgressResolver.resolveItemPacketsForUser(aupUserId);
                    }
                }
            ]
        },
        userComplianceDetailsCompliance: {
            name: `${baseStateName}.compliance`,
            url: '/compliance?page&orderBy&ascending&archived&itemPackets',
            views: {
                'all-users-progress-tab': {
                    component: MyComplianceItemsComponent
                }
            },
            params: {
                ascending: {
                    value: 'true'
                },
                itemPackets: {
                    raw: true,
                    type: 'string',
                    array: true,
                    value: []
                },
                page: {
                    raw: true,
                    type: 'int',
                    value: 1
                }
            },
            onEnter: CheckCompliancePermissions,
            resolve: [
                {
                    token: 'pagedResponse',
                    deps: ['aupUserId', AllUsersProgressResolver, Transition],
                    resolveFn: (aupUserId: string, allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) =>
                        allUsersProgressResolver.resolveUserSummary(aupUserId, transition)
                },
                {
                    token: 'currentPage',
                    deps: [Transition],
                    resolveFn: resolveCurrentPage
                }
            ]
        },
        userComplianceDetailsRequests: {
            name: `${baseStateName}.requests`,
            url: '/requests?page&searchBy&status&tag&requestType&orderBy&ascending',
            views: {
                'all-users-progress-tab': {
                    component: RequestsTabComponent
                }
            },
            params: {
                ascending: {
                    type: 'string',
                    value: 'false'
                },
                page: {
                    raw: true,
                    type: 'int',
                    value: 1
                },
                orderBy: {
                    type: 'string',
                    value: null
                }
            },
            onEnter: CheckRequestPermissions,
            resolve: [
                {
                    token: 'pagedResponse',
                    deps: ['aupUserId', 'isMyItems', AllUsersProgressResolver, Transition],
                    resolveFn: (aupUserId: string, isMyItems: boolean, allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) => {
                        return allUsersProgressResolver.resolveRequestsForUser(aupUserId, isMyItems, transition);
                    }
                },
                {
                    token: 'tags',
                    deps: [AllUsersProgressResolver],
                    resolveFn: (allUsersProgressResolver: AllUsersProgressResolver) => {
                        return allUsersProgressResolver.resolveTags();
                    }
                }
            ]
        },
        userComplianceDetailsSubmissions: {
            name: `${baseStateName}.submissions`,
            url: '/submissions/{requestId}?page&searchBy&status&act48Status&approverUserId&groupIds&requestType&orderBy&ascending',
            views: {
                'all-users-progress-tab': {
                    component: SubmissionsComponent
                }
            },
            params: {
                page: {
                    raw: true,
                    type: 'int',
                    value: 1
                },
                orderBy: {
                    value: SubmissionsSortField.LastUpdated
                },
                ascending: {
                    value: 'false'
                }
            },
            onEnter: CheckRequestPermissions,
            resolve: [{
                token: 'selectedRequest',
                deps: [AllUsersProgressResolver, Transition],
                resolveFn: (allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) => {
                    return allUsersProgressResolver.resolveSelectedRequest(transition);
                }
            },
            {
                token: 'currentPage',
                deps: [Transition],
                resolveFn: resolveCurrentPage
            }]
        },
        userCycle: {
            name: `${baseStateName}.cycle`,
            url: '/view/:cycleId?Archived&actionRequired',
            views: {
                'content@authenticated': {
                    component: ComplianceItemCycleComponent
                }
            },
            onEnter: CheckCompliancePermissions,
            resolve: [{
                token: 'details',
                deps: [AllUsersProgressResolver, Transition],
                resolveFn: (allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) => {
                    return allUsersProgressResolver.resolveUserCycleDetails(transition);
                }
            }, {
                token: 'cycleHistory',
                deps: [AllUsersProgressResolver, Transition],
                resolveFn: (allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) => {
                    return allUsersProgressResolver.resolveUserCycleHistoryByCycle(transition);
                }
            }, {
                token: 'ActionRequired',
                deps: [AllUsersProgressResolver, Transition],
                resolveFn: (allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) => {
                    return allUsersProgressResolver.resolveActionRequired(transition);
                }
            }, {
                token: 'itemPackets',
                deps: ['details', AllUsersProgressResolver],
                resolveFn: (details: CplUserCycleDetails, allUsersProgressResolver: AllUsersProgressResolver) => {
                    return allUsersProgressResolver.resolveItemPacketsForUser(details.UserCycle.UserID);
                }
            }, {
                token: 'verificationHistory',
                deps: [AllUsersProgressResolver, Transition],
                resolveFn: (allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) => {
                    return allUsersProgressResolver.resolveUserCycleVerificationHistory(transition);
                }
            }, {
                token: 'evidence',
                deps: [AllUsersProgressResolver, Transition],
                resolveFn: (allUsersProgressResolver: AllUsersProgressResolver, transition: Transition) => {
                    return allUsersProgressResolver.resolveUserCycleEvidence(transition);
                }
            }]
        }
    }
}